import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import {
  BASE_COLUMNS,
  SIZE_LABELS,
  SIZE_SPANS,
  TALLEST_ROWS,
} from "../grid.constants";
import type { TWidgetSizeKey } from "../types";

/**
 * A scale drawing of a footprint: the outline is the widest, tallest thing the
 * canvas can hold, the fill is what this size would occupy inside it. Two sizes
 * are then distinguishable without reading a word, which a "Compact / Wide /
 * Tall / Full" label row never manages — those names only mean something once
 * you already know the system.
 *
 * Two scales, because the drawing does two different jobs. In a menu row it has
 * to be *compared* against the row above and below, and 32×16 is what that
 * takes: the outline represents 12 columns, so at the 16×12 this used to be
 * drawn at, `xs` (2 of 12) came out about 2px across and `sm` (3 of 12) about
 * 3px — nobody tells a 2px bar from a 3px bar, so the one control whose entire
 * justification was "distinguishable at a glance" was, at its old size, not. At
 * 32px those become 5px and 7.5px against a 30px frame.
 *
 * On the trigger it only has to be *recognised* — it is showing one size, with
 * the name in the tooltip and the accessible name beside it — so it runs at
 * 20×10, which is what fits the chrome pill without making the cluster wider
 * than the smallest card it floats over.
 */
const SizeGlyph = ({
  size,
  scale = "menu",
}: {
  size: TWidgetSizeKey;
  scale?: "menu" | "trigger";
}) => {
  const span = SIZE_SPANS[size];

  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-start border border-current opacity-80",
        scale === "menu"
          ? "h-4 w-8 rounded-[3px] p-[1px]"
          : "h-2.5 w-5 rounded-[2px] p-px",
      )}
    >
      <span
        className="rounded-[1px] bg-current"
        style={{
          width: `${(span.w / BASE_COLUMNS) * 100}%`,
          height: `${(span.h / TALLEST_ROWS) * 100}%`,
        }}
      />
    </span>
  );
};

interface SizeMenuProps {
  widgetName: string;
  sizes: TWidgetSizeKey[];
  value: TWidgetSizeKey;
  onChange: (size: TWidgetSizeKey) => void;
}

/**
 * Direct size selection, as a menu of radio rows behind one trigger.
 *
 * The corner and edge handles are the fast path for someone already dragging;
 * this is the reliable one. It also covers touch, where a diagonal drag onto a
 * few pixels of card edge is not a realistic gesture.
 *
 * A menu rather than the inline segmented strip this replaced, for three
 * reasons that all come from the same root — a widget offers up to six sizes,
 * and six segments do not fit inside a pill capped to the width of the card
 * they float over:
 *
 * - *Fit.* The strip's answer was to scroll sideways inside the pill. On an
 *   `xs` card (2 of 12 columns) six 20px segments plus the grip and the remove
 *   button already overflowed, so the common case for the smallest widget was a
 *   control you had to swipe through to see. Behind a trigger, the chrome
 *   cluster is three fixed controls at every card size and never scrolls.
 *
 * - *Reach.* 20px segments, 2px apart, fail WCAG 2.2 SC 2.5.8 with no help from
 *   its spacing exception — there is no undisturbed 24px around any of them.
 *   Menu rows are full-width and 30px tall.
 *
 * - *Legibility.* A row has room for the scale drawing at a size where it
 *   works, the size's name, and its actual footprint in cells. The strip had
 *   room for a 12px glyph and nothing else, so five of the seven sizes were
 *   told apart by tooltip.
 *
 * Radix's radio group also supplies what the strip hand-rolled and then had to
 * document: one tab stop, arrow-key movement that carries focus, typeahead, and
 * Escape. The stray-keystroke guard is still needed, though — see `onKeyDown`.
 */
export const SizeMenu = ({
  widgetName,
  sizes,
  value,
  onChange,
}: SizeMenuProps) => {
  const { t } = useTranslation(["common"]);

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("common:dashboard_grid.size_of_widget_currently", {
                widget: widgetName,
                size: t(SIZE_LABELS[value]),
              })}
              // A 20px-tall pill rather than a 20px circle: the glyph inside it
              // is 20×10, and a circle that fits a 20px-wide glyph would be a
              // 28px control — wider than the grip and the remove button it sits
              // between, and the widest thing in a cluster that has to stay
              // narrower than an `xs` card. Height still governs the target, and
              // `.widget-target` takes that to 24px.
              className={cn(
                "widget-target widget-focus flex h-5 shrink-0 items-center rounded-full px-1",
                "text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                "data-open:bg-primary data-open:text-primary-foreground",
              )}
            >
              <SizeGlyph size={value} scale="trigger" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {t("common:dashboard_grid.size_dash", {
            size: t(SIZE_LABELS[value]),
          })}
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="end"
        side="bottom"
        // `widget-size-menu` replaces the primitive's light surface/ring/shadow
        // wholesale with the dark frosted card — see the class in global.css
        // for why this one menu breaks from the canvas' own light chrome.
        // `ring-0 shadow-none` silence the primitive's own ring and shadow so
        // only the custom ones show; `overflow-hidden` clips each row's focus
        // highlight to the card's rounded corners instead of squaring it off.
        className="widget-size-menu w-auto min-w-52 overflow-hidden rounded-2xl p-0 ring-0 shadow-none"
        // Arrow keys inside the menu must not also reach the canvas: the widget
        // frame reads Left/Right as a resize step, so an unguarded ArrowDown to
        // the next row would move the selection and resize the widget under it.
        // The strip needed this too; it is the one piece of its keyboard handling
        // the primitive cannot take over, because the primitive has no idea what
        // is listening above it.
        onKeyDown={(event) => event.stopPropagation()}
      >
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => onChange(next as TWidgetSizeKey)}
        >
          {sizes.map((size) => {
            const span = SIZE_SPANS[size];
            return (
              <DropdownMenuRadioItem
                key={size}
                value={size}
                // `widget-size-menu-item` carries the row's own color and its
                // hairline/focus states — see global.css. `rounded-none`
                // overrides the primitive's per-row rounding: rows sit flush
                // against each other, the way a long-press menu's do, with only
                // the card itself rounded.
                className="widget-size-menu-item gap-3 rounded-none px-4 py-3 text-sm"
              >
                <SizeGlyph size={size} />
                <span className="flex-1 font-medium">{SIZE_LABELS[size]}</span>
                {/* The footprint in cells, which is the one thing the drawing
                  cannot state exactly. Held clear of the check indicator's
                  own `end-2` column. */}
                <span className="widget-size-menu-caption me-4 text-widget-caption tabular-nums">
                  {span.w}×{span.h}
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
